/**
 * storageQuota.ts — localStorage Quota Safety
 *
 * Provides utilities to check remaining localStorage capacity
 * and handle quota exceeded errors gracefully.
 *
 * Addresses: PRODUCT_UX_REPORT §1.5 QA "localStorage quota exceeded — No handling"
 */

import { warn, error as logError } from './logger'

/** Estimated max localStorage size (5MB for most browsers) */
const ESTIMATED_MAX_BYTES = 5 * 1024 * 1024

/**
 * Get the current total size of all localStorage data in bytes.
 */
export function getLocalStorageUsedBytes(): number {
  let total = 0
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        // Each char is 2 bytes in UTF-16
        total += (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2
      }
    }
  } catch (err) {
    logError('Failed to calculate localStorage size:', err)
  }
  return total
}

/**
 * Get estimated remaining localStorage capacity in bytes.
 */
export function getLocalStorageRemainingBytes(): number {
  return Math.max(0, ESTIMATED_MAX_BYTES - getLocalStorageUsedBytes())
}

/**
 * Get localStorage usage as a percentage (0-100).
 */
export function getLocalStorageUsagePercent(): number {
  return Math.round((getLocalStorageUsedBytes() / ESTIMATED_MAX_BYTES) * 100)
}

/**
 * Check if localStorage is nearing capacity (>80% full).
 */
export function isLocalStorageNearFull(): boolean {
  return getLocalStorageUsagePercent() > 80
}

/**
 * Safe localStorage write with quota check.
 * Returns true if write succeeded, false if quota would be exceeded.
 *
 * If the write would push usage above 90%, it logs a warning.
 * If the write fails due to QuotaExceededError, it returns false.
 */
export function safeStorageWrite(key: string, value: string): boolean {
  try {
    // Pre-check: estimate if this write would exceed quota
    const newDataSize = (key.length + value.length) * 2
    const currentUsed = getLocalStorageUsedBytes()
    const projectedUsage = currentUsed + newDataSize

    if (projectedUsage > ESTIMATED_MAX_BYTES * 0.9) {
      warn(
        `localStorage nearing capacity: ${Math.round((projectedUsage / ESTIMATED_MAX_BYTES) * 100)}% after write to "${key}" (${Math.round(newDataSize / 1024)}KB)`,
      )
    }

    localStorage.setItem(key, value)
    return true
  } catch (err: unknown) {
    if (
      err instanceof DOMException &&
      (err.name === 'QuotaExceededError' ||
        err.code === 22 ||
        err.code === 1014)
    ) {
      logError(
        `localStorage quota exceeded when writing "${key}" (${Math.round((value.length * 2) / 1024)}KB). Current usage: ${getLocalStorageUsagePercent()}%`,
      )
      return false
    }
    logError(`Failed to write to localStorage ("${key}"):`, err)
    return false
  }
}

/**
 * Get a diagnostic summary of localStorage usage.
 * Useful for developer tools / diagnostics page.
 */
export function getStorageDiagnostics(): {
  usedBytes: number
  usedKB: number
  estimatedMaxKB: number
  usagePercent: number
  isNearFull: boolean
  topKeys: { key: string; sizeKB: number }[]
} {
  const usedBytes = getLocalStorageUsedBytes()
  const topKeys: { key: string; sizeKB: number }[] = []

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        const size = (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2
        topKeys.push({ key, sizeKB: Math.round(size / 1024) })
      }
    }
    topKeys.sort((a, b) => b.sizeKB - a.sizeKB)
  } catch (err) {
    logError('Failed to build storage diagnostics:', err)
  }

  return {
    usedBytes,
    usedKB: Math.round(usedBytes / 1024),
    estimatedMaxKB: Math.round(ESTIMATED_MAX_BYTES / 1024),
    usagePercent: getLocalStorageUsagePercent(),
    isNearFull: isLocalStorageNearFull(),
    topKeys: topKeys.slice(0, 10),
  }
}
